import React, { useState, useEffect, useRef, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CandidateSidebar from '../components/candidate/CandidateSidebar';
import CandidateMobileHeader from '../components/candidate/CandidateMobileHeader';
import CandidateMobileMenu from '../components/candidate/CandidateMobileMenu';
import { FiSearch, FiSend, FiChevronLeft } from 'react-icons/fi';
import useResponsive from '../hooks/useResponsive';
import { AuthContext } from '../context/AuthContext';
import api from '../api/api';

const CandidateInboxPage = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const { user } = useContext(AuthContext);
  
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasFetchedRef = useRef(false);
  const [conversations, setConversations] = useState([]);
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Fetch conversations on mount and poll every 5 seconds
  useEffect(() => {
    const fetchConversations = async () => {
      try {
        setLoading(true);
        const response = await api.get('/conversations');
        setApiData(response.data);
        setError(null);
      }
      catch (err) {
        console.error('Failed to fetch conversations:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    // Initial fetch
    fetchConversations();
    
    // Poll every 5 seconds for new messages
    const interval = setInterval(fetchConversations, 5000);
    
    // Cleanup on unmount
    return () => clearInterval(interval);
  }, []);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [showConversationList, setShowConversationList] = useState(!isMobile || !conversationId);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Update conversations when API data is received
  useEffect(() => {

    let conversationsToSet = [];
    
    if (apiData && apiData.data && apiData.data.conversations) {
      conversationsToSet = apiData.data.conversations;
    } else if (apiData && apiData.conversations) {
      conversationsToSet = apiData.conversations;
    } else if (!loading) {
      conversationsToSet = [];
    }
    
    // Deduplicate and MERGE conversations - but keep recruiter conversations separate
    const conversationMap = new Map();
    
    conversationsToSet.forEach(conv => {
      // Create unique key: if recruiterId exists, include it to keep separate from employer conversations
      const key = conv.recruiterId 
        ? `recruiter_${conv.recruiterId}_${conv.candidateId}`
        : `employer_${conv.employerId}_${conv.candidateId}`;
      
      const existing = conversationMap.get(key);
      
      if (existing) {
        // Merge messages from duplicate conversation
        const existingMessages = existing.messages || [];
        const newMessages = conv.messages || [];
        const allMessages = [...existingMessages, ...newMessages];
        
        // Deduplicate messages by ID
        const uniqueMessages = Array.from(
          new Map(allMessages.map(msg => [msg.id, msg])).values()
        ).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        
        existing.messages = uniqueMessages;
        existing.lastMessage = conv.lastMessage || existing.lastMessage;
        existing.lastMessageTime = conv.lastMessageTime || existing.lastMessageTime;
        existing.unreadCount = allMessages.filter(m => !m.read).length;
        
        // Keep the NEWEST conversation ID (most recent activity)
        if (new Date(conv.lastMessageTime) > new Date(existing.lastMessageTime)) {
          existing.id = conv.id;
        }
      } else {
        conversationMap.set(key, { ...conv });
      }
    });
    
    const mergedConversations = Array.from(conversationMap.values());

    setConversations(prev => {
      // Update selected conversation if it exists in the new data
      if (selectedConversation) {
        const updatedSelected = mergedConversations.find(c => c.id === selectedConversation.id);
        if (updatedSelected && updatedSelected.messages.length !== selectedConversation.messages?.length) {

          setSelectedConversation(updatedSelected);
        }
      }
      return mergedConversations;
    });
  }, [apiData, loading, user]);
  
  // Ensure this page is only accessible to candidate users
  useEffect(() => {
    // Check if user exists and has a userType
    if (user) {
      // If user is not a candidate, redirect to appropriate dashboard
      if (user.userType === 'employer') {

        navigate('/employer/dashboard');
      }
    }
  }, [user, navigate]);

  useEffect(() => {
    // If there's a conversationId in the URL, find and select it.
    // Otherwise, default to the first conversation.
    const conversation = conversationId 
      ? conversations.find(c => c.id.toString() === conversationId)
      : conversations[0];
    setSelectedConversation(conversation || null);
    
    // On mobile, hide conversation list when a conversation is selected
    if (isMobile && conversation) {
      setShowConversationList(false);
    }
  }, [conversationId, conversations, isMobile]);

  useEffect(() => {
    // Auto-scroll to the latest message
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedConversation?.messages]);

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    if (isMobile) {
      setShowConversationList(false);
    }
  };
  
  const handleBackToList = () => {
    setShowConversationList(true);
  };

  const handleDeleteConversation = async (conversationId) => {
    if (!window.confirm('Are you sure you want to delete this conversation? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/conversations/${conversationId}`);
      
      // Remove from local state
      setConversations(prev => prev.filter(c => c.id !== conversationId));
      
      // If deleted conversation was selected, clear selection and select first remaining conversation
      if (selectedConversation?.id === conversationId) {
        const remainingConversations = conversations.filter(c => c.id !== conversationId);
        setSelectedConversation(remainingConversations[0] || null);
      }

    } catch (err) {
      console.error('[ERROR] Failed to delete conversation:', err);
      alert('Failed to delete conversation. Please try again.');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation || sendingMessage) return;

    const messageData = {
      conversationId: selectedConversation.id,
      content: newMessage,
      sender: 'candidate'
    };

    try {
      setSendingMessage(true);
      
      // Send message to API
      await api.post('/messages', messageData);
      
      // Create temporary message object for immediate UI update
      const message = {
        sender: 'candidate',
        text: newMessage,
        timestamp: new Date().toISOString()
      };

      // Update local state for immediate feedback
      const updatedConversations = conversations.map(convo => {
        if (convo.id === selectedConversation.id) {
          return { 
            ...convo, 
            messages: [...convo.messages, message], 
            lastMessage: newMessage, 
            timestamp: 'Just now' 
          };
        }
        return convo;
      });

      setConversations(updatedConversations);
      setNewMessage('');
      
      // Refetch the conversation to get the server's version
      await refetchConversation(selectedConversation.id);
    } catch (err) {
      console.error('Failed to send message:', err);
      alert('Failed to send message. Please try again.');
    } finally {
      setSendingMessage(false);
    }
  };
  
  const refetchConversation = async (conversationId) => {
    try {
      const response = await api.get(`/messages?conversationId=${conversationId}`);

      // Extract messages from response (could be nested in data.data)
      const messagesData = response.data.data || response.data;
      
      // Update the specific conversation with fresh data
      setConversations(prevConversations => {
        return prevConversations.map(convo => {
          if (convo.id === conversationId) {
            return {
              ...convo,
              messages: messagesData.messages || [],
              lastMessage: messagesData.lastMessage || convo.lastMessage,
              timestamp: messagesData.timestamp || convo.timestamp,
              unread: 0 // Mark as read since we're viewing it
            };
          }
          return convo;
        });
      });
    } catch (err) {
      console.error('Failed to refetch conversation:', err);
    }
  };

  return (
    <div className="flex bg-white min-h-screen font-sans">
      <CandidateSidebar />
      {isMobile && mobileMenuOpen && <CandidateMobileMenu setMobileMenuOpen={setMobileMenuOpen} />}
      <main className="flex-1 flex flex-col h-screen overflow-hidden ml-0 md:ml-24">
        {isMobile ? (
          <CandidateMobileHeader mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
        ) : (
          <div className="pt-4 px-4 md:pt-6 md:px-8 pb-4 bg-white shadow-sm">
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-black mb-1">Messages</h1>
              <p className="text-gray-600">Manage your conversations</p>
            </div>
          </div>
        )}
        
        <div className="flex-grow flex flex-col md:flex-row overflow-hidden mx-4 md:mx-8 mt-6">
          {/* Conversation List - Sidebar on mobile, fixed width on desktop */}
          <div className={`${isMobile && !showConversationList ? 'hidden' : 'flex'} md:w-80 lg:w-96 bg-white flex-col md:max-h-full overflow-hidden rounded-lg border border-gray-200 md:mr-4 mb-4 md:mb-0`} 
            style={{ 
              boxShadow: '3px 3px 0 rgba(0, 0, 0, 0.8)',
              transform: 'translateX(-3px) translateY(-3px)'
            }}>
            <div className="p-5 border-b">
              <h2 className="text-xl font-bold text-black mb-4">Conversations</h2>
              <div className="relative">
                <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-black" size={18} />
                <input 
                  type="text" 
                  placeholder="Search messages..." 
                  className="w-full pl-12 pr-4 py-3 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-black focus:ring-opacity-50 transition-all" 
                  style={{ boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)' }}
                />
              </div>
            </div>
            <div className="flex-grow overflow-y-auto">
              {conversations.length > 0 ? (
                conversations.map(convo => (
                  <ConversationItem 
                    key={convo.id} 
                    conversation={convo} 
                    isSelected={selectedConversation?.id === convo.id}
                    onSelect={() => handleSelectConversation(convo)}
                  />
                ))
              ) : (
                <div className="p-4 text-center text-gray-500">No conversations yet</div>
              )}
            </div>
          </div>

          {/* Message View - Takes remaining space */}
          <div className={`${isMobile && showConversationList ? 'hidden' : 'flex'} flex-1 flex-col bg-white ml-0 rounded-lg border border-gray-200 transform transition-all`} 
            style={{ 
              boxShadow: '3px 3px 0 rgba(0, 0, 0, 0.8)',
              transform: 'translateX(-3px) translateY(-3px)'
            }}>
            {selectedConversation ? (
              <>
                {/* Conversation Header */}
                <div className="p-4 md:p-5 border-b flex items-center gap-4 bg-white">
                  {isMobile && (
                    <button 
                      onClick={handleBackToList}
                      className="mr-2 p-1 rounded-full hover:bg-gray-100"
                    >
                      <FiChevronLeft size={20} />
                    </button>
                  )}
                  <div className="relative">
                    <img 
                      src={selectedConversation.recruiterAvatar || selectedConversation.employerAvatar || '/images/default-employer.png'} 
                      alt={selectedConversation.recruiterName || selectedConversation.employerName || 'Employer'} 
                      className="w-12 h-12 object-cover border border-black relative z-10" 
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/images/default-employer.png';
                      }}
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-black">
                      {selectedConversation.recruiterName || selectedConversation.employerName || 'Employer'}
                    </h3>
                    <div className="flex items-center">
                      <span className="w-2 h-2 bg-black mr-2"></span>
                      <p className="text-sm text-black">{selectedConversation.jobTitle}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => handleDeleteConversation(selectedConversation.id)}
                      className="p-2 text-red-600 hover:bg-red-50 transition-colors rounded-md"
                      title="Delete conversation"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
                
                {/* Messages Container */}
                <div className="flex-grow p-4 md:p-6 overflow-y-auto bg-white space-y-4 md:space-y-5">
                  {selectedConversation.messages.length > 0 ? (
                    selectedConversation.messages.map((msg, index) => (
                      <Message 
                        key={index} 
                        message={msg} 
                        currentUserId={user?.id}
                        isFirstMessage={index === 0}
                        isLastMessage={index === selectedConversation.messages.length - 1}
                      />
                    ))
                  ) : (
                    <div className="text-center py-12 px-6">
                      <div className="bg-white p-8 border border-black max-w-md mx-auto">
                        <div className="bg-white border border-black p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                        </div>
                        <h3 className="text-lg font-bold text-black mb-2">Start the conversation</h3>
                        <p className="text-black">Send your first message to begin chatting with {selectedConversation.employerName || 'the employer'}.</p>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
                
                {/* Message Input */}
                <div className="p-3 md:p-4 bg-white border-t">
                  <form onSubmit={handleSendMessage} className="flex items-center gap-2 md:gap-3">
                    <div className="flex-grow relative">
                      <input 
                        type="text" 
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type a message..." 
                        className="w-full border border-black py-3 px-5 pr-12 focus:outline-none transition-all"
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 flex gap-1">
                        <button type="button" className="text-black p-1">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </button>
                        <button type="button" className="text-black p-1">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <button 
                      type="submit" 
                      disabled={!newMessage.trim() || sendingMessage}
                      className="bg-black text-white p-3 border border-black disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      {sendingMessage ? (
                        <span className="inline-block animate-spin">⌛</span>
                      ) : (
                        <FiSend size={18} />
                      )}
                    </button>
                  </form>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full text-black flex-col p-8">
                <div className="bg-white border border-black p-8 mb-6">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-black mb-2">Your messages</h3>
                <p className="text-black text-center max-w-sm">Select a conversation from the list to start chatting with candidates.</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

const ConversationItem = ({ conversation, isSelected, onSelect }) => {
  const hasUnread = conversation.unread > 0;
  
  return (
    <div 
      onClick={onSelect} 
      className={`p-4 flex items-center gap-4 cursor-pointer transition-all duration-200 border-b border-black
        ${isSelected ? 'bg-black text-white' : 'hover:bg-gray-100'}
        ${hasUnread && !isSelected ? 'bg-gray-100' : ''}`
      }
    >
      <div className="relative">
        <img 
          src={conversation.recruiterAvatar || conversation.employerAvatar || '/images/default-employer.png'} 
          alt={conversation.recruiterName || conversation.employerName || 'Employer'} 
          className="w-12 h-12 object-cover border border-black relative z-10" 
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = '/images/default-employer.png';
          }}
        />
        {hasUnread && (
          <span className="absolute -top-1 -right-1 bg-black text-white text-xs w-6 h-6 flex items-center justify-center border border-white z-20">
            {conversation.unread}
          </span>
        )}
      </div>
      <div className="flex-grow overflow-hidden">
        <div className="flex justify-between items-center">
          <p className={`font-bold truncate ${isSelected ? 'text-white' : hasUnread ? 'text-black' : 'text-black'}`}>
            {conversation.recruiterName || conversation.employerName || 'Employer'}
          </p>
          <p className={`text-xs ${isSelected ? 'text-white' : hasUnread ? 'bg-black text-white px-2 py-1' : 'text-black'}`}>
            {conversation.timestamp}
          </p>
        </div>
        <div className="flex justify-between items-center mt-1">
          <p className={`text-sm truncate ${isSelected ? 'text-white' : hasUnread ? 'text-black font-medium' : 'text-black'}`}>
            {conversation.lastMessage}
          </p>
          {hasUnread && !isSelected && (
            <span className="w-2 h-2 bg-black flex-shrink-0 ml-2"></span>
          )}
        </div>
        <div className="flex items-center mt-1">
          <svg xmlns="http://www.w3.org/2000/svg" className={`h-3 w-3 ${isSelected ? 'text-white' : 'text-black'} mr-1`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <p className={`text-xs ${isSelected ? 'text-white' : 'text-black'} truncate`}>{conversation.jobTitle}</p>
        </div>
      </div>
    </div>
  );
};

const Message = ({ message, currentUserId }) => {
  // Check if the current user sent this message
  const isSentByMe = message.senderId === currentUserId;
  
  return (
    <div className={`flex ${isSentByMe ? 'justify-end' : 'justify-start'}`}>
      <div 
        className={`relative max-w-xs sm:max-w-md md:max-w-lg px-5 py-3
          ${isSentByMe ? 
            'bg-black text-white border border-black mr-2' : 
            'bg-white text-black border border-black ml-2'}
        `}
      >
        <p className="whitespace-pre-wrap break-words">{message.text}</p>
        <span 
          className={`text-xs block mt-2 ${isSentByMe ? 'text-right text-white' : 'text-black'}`}
        >
          {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </span>
      </div>
    </div>
  );
};

export default CandidateInboxPage;
